import { useEffect, useState } from "react";
import { API } from "../../../services/api";
import {
    availableCreateServiceTypes as availableCreateServiceTypesFor,
    EMPTY_SERVICE_FORM,
    servicePatchPayload,
    serviceTypeOptionsFor as serviceTypeOptionsAmong,
    withValidCreateType,
} from "../serviceRules";

const servicesUrl = pharmacyId => `/super-admin/pharmacies/${pharmacyId}/services`;

export function useIndicatorServices(pharmacyId, providedServices) {
    const [services, setServices] = useState(providedServices || []);
    const [loading, setLoading] = useState(!providedServices);

    useEffect(() => {
        if (providedServices) {
            setServices(providedServices);
            setLoading(false);
            return undefined;
        }
        let mounted = true;
        setLoading(true);
        API.get(servicesUrl(pharmacyId))
            .then(({ data }) => { if (mounted) setServices(data || []); })
            .catch(() => { if (mounted) setServices([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [pharmacyId, providedServices]);

    return { services, loading };
}

function useServiceList(pharmacyId) {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);
        API.get(servicesUrl(pharmacyId))
            .then(({ data }) => { if (mounted) setServices(data || []); })
            .catch(() => { if (mounted) setError("Impossible de charger les services."); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [pharmacyId]);

    return { services, setServices, loading, error, setError };
}

export function useServiceManager(pharmacyId) {
    const { services, setServices, loading, error, setError } = useServiceList(pharmacyId);
    const [form, setForm] = useState(EMPTY_SERVICE_FORM);
    const [saving, setSaving] = useState(false);

    const createService = async () => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await API.post(servicesUrl(pharmacyId), form);
            setServices(prev => [...prev, data]);
            setForm(EMPTY_SERVICE_FORM);
        } catch (err) {
            setError(err.response?.data?.message ?? "Erreur lors de la création du service.");
        } finally {
            setSaving(false);
        }
    };

    const availableCreateServiceTypes = availableCreateServiceTypesFor(services);

    useEffect(() => {
        if (withValidCreateType(form, availableCreateServiceTypes) !== form) {
            setForm(current => withValidCreateType(current, availableCreateServiceTypes));
        }
    }, [availableCreateServiceTypes, form]);

    const patchService = async (service, patch) => {
        try {
            const { data } = await API.patch(`${servicesUrl(pharmacyId)}/${service.id}`, servicePatchPayload(service, patch));
            setServices(prev => prev.map(item => item.id === service.id ? data : item));
        } catch (err) {
            setError(err.response?.data?.message ?? "Erreur lors de la modification du service.");
        }
    };

    return {
        services,
        loading,
        error,
        form,
        setForm,
        saving,
        createService,
        patchService,
        availableCreateServiceTypes,
        serviceTypeOptionsFor: service => serviceTypeOptionsAmong(services, service),
    };
}
